import React, { Fragment } from 'react'
import {
  Table,
  TableBody,
  TableContainer
} from '@mui/material'
import { styled } from '@mui/material/styles'
import { GenericTableV2Props, TableData } from './types'
import { TableHeader } from './components/TableHeader'
import { TableRowComponent } from './components/TableRowComponent'
import { EmptyState } from './components/EmptyState'
import { LoadingState } from './components/LoadingState'
import { TablePagination } from './components/TablePagination'
import { useTableExpansion } from './hooks/useTableExpansion'

const StyledTableContainer = styled('div')(({ theme }) => ({
  width: '100%',
  marginTop: theme.spacing(0),
  marginBottom: '-16px'
}))

const StyledTable = styled(Table)(({ theme }) => ({
  minWidth: 650,
  '& .MuiTableCell-root': {
    padding: '10px 7px'
  },
  '& .MuiTableBody-root .MuiTableCell-root': {
    borderBottom: `1px solid ${theme.palette.divider}`
  }
}))

export const GenericTableV2 = <T extends TableData>({
  data,
  columns,
  keyField,
  loading = false,
  emptyMessage = 'Ajuste los criterios de búsqueda para mostrar resultados',
  emptyIcon,
  noResultsComponent,
  selection,
  sorting,
  actions,
  prioritization,
  pagination,
  styling,
  expandable,
  onRowClick,
  onRowDoubleClick
}: GenericTableV2Props<T>) => {

  // Hook para manejo de expansión (solo si expandable está configurado)
  const expansionHook = useTableExpansion({
    data,
    keyField,
    expandable,
    currentPage: pagination?.page
  })

  // Estados de carga y datos vacíos
  if (loading) {
    return <LoadingState />
  }

  if (!data || data.length === 0) {
    if (noResultsComponent) {
      return <Fragment>{noResultsComponent}</Fragment>
    }
    return <EmptyState message={emptyMessage} icon={emptyIcon} />
  }

  const hasActions = actions && actions.length > 0

  return (
    <StyledTableContainer>
      <TableContainer>
        <StyledTable
          size={styling?.dense ? 'small' : 'medium'}
        >
          <TableHeader
            columns={columns}
            selection={selection}
            sorting={sorting}
            prioritization={prioritization}
            expandable={expandable}
            hasActions={Boolean(hasActions)}
            data={data}
            allRowsExpanded={expansionHook.allRowsExpanded}
            hasExpandableRows={expansionHook.stats.hasExpandableRows}
            onToggleAllRows={expansionHook.toggleAllRows}
          />

          <TableBody>
            {data.map((row, index) => {
              const rowId = expansionHook.createUniqueId(row, index)
              return (
                <TableRowComponent
                  key={String(row[keyField])}
                  row={row}
                  index={index}
                  columns={columns}
                  keyField={keyField}
                  selection={selection}
                  prioritization={prioritization}
                  styling={styling}
                  expandable={expandable}
                  actions={actions}
                  onRowClick={onRowClick}
                  onRowDoubleClick={onRowDoubleClick}
                  rowId={rowId}
                  isRowExpanded={expansionHook.isRowExpanded(rowId)}
                  hasDetails={expansionHook.hasDetails(row)}
                  onToggleRowExpansion={() =>
                    expansionHook.toggleRowExpansion(rowId)
                  }
                  getDetailData={expansionHook.getDetailData}
                />
              )
            })}
          </TableBody>
        </StyledTable>
      </TableContainer>

      {pagination && <TablePagination pagination={pagination} />}
    </StyledTableContainer>
  )
}
